'use client'

import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'

import { cn } from '../../lib/utils'
import { Icon } from '../icon'
import { Typography } from '../typography'
import { useDirection } from '../../hooks'

const colorVariants = {
  base: {
    range: 'bg-base-surface-80',
    thumb: 'border-base-main ring-base-main bg-base-main',
  },
  positive: {
    range: 'bg-positive-surface-80',
    thumb: 'border-positive-main ring-positive-main bg-positive-main',
  },
  positiveLight: {
    range: 'bg-positive-main',
    thumb: 'border-positive-main ring-positive-main bg-positive-main',
  },
  neutral: {
    range: 'bg-neutral-surface-80',
    thumb: 'border-neutral-main ring-neutral-main bg-neutral-main',
  },
  negative: {
    range: 'bg-negative-surface-80',
    thumb: 'border-negative-main ring-negative-main bg-negative-main',
  },
  custom: {
    range: '',
    thumb: '',
  },
}

const thumbSizeVariants = {
  md: 'size-4 ',
  sm: 'size-3',
}
const inactiveThumbSizeVariants = {
  md: 'size-1',
  sm: 'size-1',
}

const baseThumbStyles =
  'block shrink-0 rounded-full border shadow-sm transition-all duration-200 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50'

const hoverThumbStyles = {
  md: 'hover:w-6 hover:h-4 hover:rounded-md hover:ring-4 focus-visible:ring-4',
  sm: 'hover:w-5 hover:h-3 hover:rounded-sm  hover:ring-3 focus-visible:ring-3',
}

interface SliderProps
  extends React.ComponentProps<typeof SliderPrimitive.Root> {
  color?: keyof typeof colorVariants
  rangeClassName?: string
  thumbClassName?: string
  noThumb?: boolean
  thumbSize?: keyof typeof thumbSizeVariants
  showInactiveThumbs?: boolean
  showTooltip?: boolean
}

function Slider({
  className,
  rangeClassName,
  thumbClassName,
  defaultValue,
  value,
  min = 0,
  max = 100,
  color = 'positive',
  noThumb = false,
  thumbSize = 'md',
  showInactiveThumbs = false,
  showTooltip = true,
  ...props
}: SliderProps) {
  const direction = useDirection()
  const [isDragging, setIsDragging] = React.useState(false)

  const _values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min, max],
    [value, defaultValue, min, max],
  )

  return (
    <SliderPrimitive.Root
      data-slot='slider'
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        'relative  flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
        className,
      )}
      onTouchStart={() => setIsDragging(true)}
      onTouchEnd={() => setIsDragging(false)}
      onMouseEnter={() => setIsDragging(true)}
      onMouseLeave={() => setIsDragging(false)}
      {...props}
    >
      <div className='relative flex items-center w-full py-3'>
        <SliderPrimitive.Track
          data-slot='slider-track'
          className={cn(
            'bg-neutral-surface-dark relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5',
          )}
        >
          <SliderPrimitive.Range
            data-slot='slider-range'
            className={cn(
              'absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full',
              colorVariants[color].range,
              rangeClassName,
            )}
          />
        </SliderPrimitive.Track>
      </div>
      {!noThumb && (
        <>
          {/* Active thumbs */}
          {Array.from({ length: _values.length }, (_, index) => {
            const thumbValue = _values[index] ?? min
            const percentage = Math.round(
              ((thumbValue - min) / (max - min)) * 100,
            )

            const thumbElement = (
              <SliderPrimitive.Thumb
                data-slot='slider-thumb'
                key={`active-${index}`}
                className={cn(
                  baseThumbStyles,
                  colorVariants[color].thumb,
                  thumbClassName,
                  thumbSizeVariants[thumbSize],
                  !showInactiveThumbs && hoverThumbStyles[thumbSize],
                  'group relative',
                )}
              >
                {/* Arrow icons that appear on hover */}
                {!showInactiveThumbs && (
                  <div className='absolute inset-0 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none'>
                    <Icon
                      name='ChevronRight'
                      size='xs'
                      color='fill-neutral-surface-40'
                      className='w-sm'
                    />
                    <Icon
                      name='ChevronLeft'
                      size='xs'
                      color='fill-neutral-surface-40'
                      className='w-sm'
                    />
                  </div>
                )}
              </SliderPrimitive.Thumb>
            )

            if (showTooltip && !showInactiveThumbs) {
              return (
                <TooltipPrimitive.Provider
                  key={`tooltip-${index}`}
                  delayDuration={0}
                >
                  <TooltipPrimitive.Root open={isDragging}>
                    <TooltipPrimitive.Trigger asChild>
                      {thumbElement}
                    </TooltipPrimitive.Trigger>
                    <TooltipPrimitive.Portal>
                      <TooltipPrimitive.Content
                        sideOffset={5}
                        className='bg-neutral-surface-100   text-neutral-surface-light px-2 py-1 rounded text-xs font-medium shadow-lg border border-neutral-surface-20'
                        side='top'
                        align='center'
                      >
                        <Typography size='xs' className='font-[YekanBakh]'>
                          {percentage || 0}%
                        </Typography>
                        <TooltipPrimitive.Arrow className='fill-neutral-surface-100' />
                      </TooltipPrimitive.Content>
                    </TooltipPrimitive.Portal>
                  </TooltipPrimitive.Root>
                </TooltipPrimitive.Provider>
              )
            }

            return thumbElement
          })}
          {showInactiveThumbs &&
            Array.from({ length: max + 1 }, (_, index) => {
              const thumbValue = index
              const isActive = _values.includes(thumbValue)

              return (
                <div
                  key={`inactive-${index}`}
                  className={cn(
                    inactiveThumbSizeVariants[thumbSize],
                    'pointer-events-none',
                    'bg-base-surface-60',
                    'rounded-full',
                    'absolute',
                    'shadow-sm',
                    isActive ? 'opacity-0' : 'opacity-100',
                  )}
                  style={{
                    [`${direction === 'rtl' ? 'right' : 'left'}`]: `${(thumbValue / max) * 100}%`,
                    transform: 'translateX(-50.9%)',
                  }}
                />
              )
            })}
        </>
      )}
    </SliderPrimitive.Root>
  )
}

export { Slider }
export type { SliderProps }
